import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subscription } from "rxjs";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";

/**
 * MAIA wizard — Step 3 of 4 (Enhancing portfolio data).
 *
 * Progress screen shown while the free-overlay stage (auto) and, when selected, the paid-overlay
 * stage fetch and write their fields. Both run in the background and can overlap, so completion is
 * read from per-row stage status via getOverlayProgress (the authoritative signal), driven by the
 * job SSE 'done' events and backed by a slow safety re-check so a missed event can't hang the screen.
 * Only advances to "Portfolio ready" once free (and paid, if the user selected it) are complete.
 */
@Component({
    selector: "maia-enhance-step",
    templateUrl: "./maia-enhance-step.component.html",
    styleUrls: ["./maia-enhance-step.component.scss"],
})
export class MaiaEnhanceStepComponent implements OnInit, OnDestroy {
    @Input() datasetId: string | null = null;
    @Input() jobId: number | null = null;
    @Input() fileName = "";
    @Input() totalLocations = 0;
    /** When true the user picked paid overlays, so paid completion is also required before continuing. */
    @Input() paidSelected = false;

    @Output() readonly closeRequested = new EventEmitter<void>();
    @Output() readonly completed = new EventEmitter<void>();

    percent = 0;
    statusMessage = "Enriching your portfolio…";
    errorMessage: string | null = null;
    done = false;

    private events?: Subscription;
    private safety?: ReturnType<typeof setInterval>;
    private reconnectTimer?: ReturnType<typeof setTimeout>;

    /** Delay before reopening the SSE stream, so an immediately-closing stream can't tight-loop (reconnect storm). */
    private static readonly RECONNECT_DELAY_MS = 4000;

    constructor(private readonly maiaUploadService: MaiaUploadService) {}

    ngOnInit(): void {
        if (this.jobId == null || !this.datasetId) {
            return;
        }
        // Source of truth = per-row overlay status. SSE 'done' events drive an immediate re-check;
        // the interval is only a backstop for a missed event (the job stream is closed on the first
        // stage's completion while the other stage may still be running).
        this.checkProgress();
        this.subscribe();
        this.safety = setInterval(() => this.checkProgress(), 4000);
    }

    ngOnDestroy(): void {
        this.teardown();
    }

    /** Total unknown / not started yet -> animate an indeterminate bar. */
    get indeterminate(): boolean {
        return !this.done && !this.errorMessage && this.percent <= 0;
    }

    private subscribe(): void {
        if (this.jobId == null) {
            return;
        }
        this.events = this.maiaUploadService.streamPaidOverlayEvents(this.jobId).subscribe({
            next: (event) => {
                if (event.data && event.data.message) {
                    this.statusMessage = event.data.message;
                }
                this.checkProgress();
            },
            error: () => this.resubscribe(),
            complete: () => {
                this.checkProgress();
                this.resubscribe();
            },
        });
    }

    /**
     * A stage finishing closes the whole job stream (completeAll); reopen after a short delay so the
     * other stage's events still arrive. The delay is essential: a stream that closes immediately
     * (e.g. no active job after a backend restart) would otherwise reconnect in a tight loop and
     * flood the server with preflight requests. Completion is still detected by the safety re-check.
     */
    private resubscribe(): void {
        if (this.done || this.errorMessage || this.reconnectTimer) {
            return;
        }
        if (this.events) {
            this.events.unsubscribe();
            this.events = undefined;
        }
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            if (!this.done && !this.errorMessage) {
                this.subscribe();
            }
        }, MaiaEnhanceStepComponent.RECONNECT_DELAY_MS);
    }

    private checkProgress(): void {
        if (this.jobId == null || !this.datasetId || this.done) {
            return;
        }
        this.maiaUploadService.getOverlayProgress(this.datasetId, this.jobId).subscribe({
            next: (p) => {
                const remaining = p.freeRemaining + (this.paidSelected ? p.paidRemaining : 0);
                if (p.total > 0) {
                    this.percent = Math.min(99, Math.max(this.percent, Math.round(((p.total - remaining) / p.total) * 100)));
                }
                if (p.freeDone && (!this.paidSelected || p.paidDone)) {
                    this.finish();
                }
            },
            error: () => {
                // Transient - the next SSE event or the safety tick retries.
            },
        });
    }

    private finish(): void {
        this.done = true;
        this.percent = 100;
        this.teardown();
        this.completed.emit();
    }

    private teardown(): void {
        if (this.events) {
            this.events.unsubscribe();
            this.events = undefined;
        }
        if (this.safety) {
            clearInterval(this.safety);
            this.safety = undefined;
        }
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    }
}
